/**
 * usageTelemetry.ts — 利用ログの記録（ページビュー・滞在時間・主要ボタン）
 *
 * 日記分析オーバーホール Phase 5「利用ログ修復＋無駄画面集計」(2026-05)。
 * tab_clicks への送信が 2026-04-02 で停止していた問題を直す。週1バッチ
 * `.claude/hooks/usage-dead-zone-summary.sh` が「30日表示ゼロのページ /
 * クリックゼロのボタン / 直帰の多いページ」を ceo_insights に書く。
 *
 * 設計メモ:
 *   - ボタンは page='button:<name>' の prefix で同じ tab_clicks に混ぜる
 *     （新テーブルを増やさない。集計側で regex 振り分け）。
 *   - localStorage にバッファして、unmount / 60秒ごと / visibilitychange で
 *     まとめてフラッシュ（1イベント1リクエストは負荷高い）。
 *   - dwell は最終値のみ tab_clicks.last_dwell_seconds に保持。1秒未満は捨てる
 *     （チラ見ノイズ）。
 *   - サーバ書き込みは `record_usage(p_page, p_dwell_seconds)` RPC。RLS は
 *     auth.uid() で固定（body での user_id 改ざんを許さない）。
 */

import { useEffect, useRef } from 'react'
import { supabase } from '@/lib/supabase'

/** 1イベント。同じ (page) に dwell が複数来てもサーバ側は最終値で上書きされる。 */
interface PendingEvent {
  /** 'home' / 'journal' / ... のページ名、または 'button:<name>'。 */
  page: string
  /** 滞在時間（秒）。ボタンクリックや純粋なビュー発火では undefined。 */
  dwellSeconds?: number
}

const LS_KEY = 'fy:usage-telemetry-buffer:v1'
const MAX_BUFFER = 50
const FLUSH_INTERVAL_MS = 60_000
/** ノイズ抑制: ページ滞在 1秒未満は dwell として記録しない（クリック貫通対策）。 */
const MIN_DWELL_SECONDS = 1
/** ノイズ抑制: 異常値（1日以上画面開きっぱなし放置）は記録しない。 */
const MAX_DWELL_SECONDS = 6 * 60 * 60 // 6h

/* ─────────────────── module state ─────────────────── */

let flushTimer: ReturnType<typeof setInterval> | null = null
let initialized = false
let flushingPromise: Promise<void> | null = null

/* ─────────────────── buffer (localStorage backed) ─────────────────── */

function loadBuffer(): PendingEvent[] {
  try {
    const raw = localStorage.getItem(LS_KEY)
    if (!raw) return []
    const arr = JSON.parse(raw)
    if (!Array.isArray(arr)) return []
    return arr.filter((e): e is PendingEvent => !!e && typeof e.page === 'string')
  } catch {
    return []
  }
}

function saveBuffer(buf: PendingEvent[]): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(buf.slice(-MAX_BUFFER)))
  } catch {
    /* localStorage 不可能環境（プライベートブラウズ等）— サイレントに諦める */
  }
}

function pushEvent(ev: PendingEvent): void {
  if (typeof window === 'undefined') return
  const buf = loadBuffer()
  buf.push(ev)
  saveBuffer(buf)
  // バッファが膨らんできたら即フラッシュ（取りこぼし対策）
  if (buf.length >= MAX_BUFFER) {
    void flushUsageTelemetry()
  }
}

/* ─────────────────── flush ─────────────────── */

/**
 * バッファ済みイベントをサーバに送る。同一 page のイベントは最後のものに統合
 * （dwell は最終値、ビュー/クリック発火回数は数えて累積で送る）。
 *
 * 並行呼び出しは握りつぶし、進行中の Promise を共有する（visibilitychange と
 * unmount が同時に発火しても二重送信しない）。
 */
export async function flushUsageTelemetry(): Promise<void> {
  if (flushingPromise) return flushingPromise
  flushingPromise = (async () => {
    try {
      const buf = loadBuffer()
      if (buf.length === 0) return

      // 楽観的に buffer をクリア。失敗したら戻す。
      saveBuffer([])

      // 同じ page を集約: dwell は最終値、発火回数は count として保持
      // → ただし record_usage RPC は1回呼ぶごとに click_count++ する仕様なので、
      //   集約はせず順番に投げる（多すぎる場合は最後の MAX_BUFFER 件だけ）。
      const toSend = buf.slice(-MAX_BUFFER)
      const failed: PendingEvent[] = []

      for (const ev of toSend) {
        try {
          const dwell =
            typeof ev.dwellSeconds === 'number' &&
            ev.dwellSeconds >= MIN_DWELL_SECONDS &&
            ev.dwellSeconds <= MAX_DWELL_SECONDS
              ? Math.round(ev.dwellSeconds)
              : null
          const { error } = await supabase.rpc('record_usage', {
            p_page: ev.page,
            p_dwell_seconds: dwell,
          })
          if (error) failed.push(ev)
        } catch {
          failed.push(ev)
        }
      }

      // 失敗分は次回に持ち越す
      if (failed.length > 0) {
        const existing = loadBuffer()
        saveBuffer([...existing, ...failed])
      }
    } finally {
      flushingPromise = null
    }
  })()
  return flushingPromise
}

/** モジュール初期化: 定期フラッシュと visibilitychange リスナを1度だけ仕込む。 */
function ensureInit(): void {
  if (initialized) return
  if (typeof window === 'undefined') return
  initialized = true

  flushTimer = setInterval(() => {
    void flushUsageTelemetry()
  }, FLUSH_INTERVAL_MS)

  // タブ非アクティブ化 / 閉じる時にフラッシュ
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      void flushUsageTelemetry()
    }
  })
  window.addEventListener('pagehide', () => {
    void flushUsageTelemetry()
  })
}

/* ─────────────────── public API ─────────────────── */

/**
 * ページビューを記録（dwell なしの「開いた」イベント）。
 * 同じページの hook 内 mount でも1回しか呼ばれない設計なので、ここでは
 * click_count を1進めて last_clicked_at を更新する役割。
 */
export function recordPageView(page: string): void {
  if (!page) return
  ensureInit()
  pushEvent({ page })
}

/**
 * ページ滞在時間を記録。unmount 時に startMs を見て呼ぶ。
 * 1秒未満は捨てる（チラ見ノイズ）。サーバ側で「click_count++ + dwell 更新」
 * の UPSERT になる。
 */
export function recordPageDwell(page: string, startMs: number): void {
  if (!page || !startMs) return
  const seconds = Math.round((Date.now() - startMs) / 1000)
  if (seconds < MIN_DWELL_SECONDS) return
  if (seconds > MAX_DWELL_SECONDS) return
  ensureInit()
  pushEvent({ page, dwellSeconds: seconds })
}

/**
 * 主要ボタンのクリックを記録。`button:<name>` 形式で tab_clicks に混ぜる
 * （新テーブルを増やさない）。<name> はスネークケース推奨（例:
 * 'self_doc_open', 'partner_send', 'journal_save', 'calendar_sync_todo'）。
 */
export function recordButtonClick(button: string): void {
  if (!button) return
  const page = button.startsWith('button:') ? button : `button:${button}`
  ensureInit()
  pushEvent({ page })
}

/* ─────────────────── React hook: ページ計測 ─────────────────── */

/**
 * ページコンポーネントの最上部で1度だけ呼ぶ。
 *   - mount 時に recordPageView を発火（click_count 進める）
 *   - unmount 時に recordPageDwell を発火（last_dwell_seconds 更新）
 * page を変えると別ページ扱いになる（通常は固定文字列で OK）。
 */
export function usePageView(page: string): void {
  const startRef = useRef<number>(0)
  useEffect(() => {
    if (!page) return
    const start = Date.now()
    startRef.current = start
    recordPageView(page)
    return () => {
      recordPageDwell(page, start)
      // unmount は遷移直前なので、ここで明示フラッシュ（バッファに溜める前に出す）
      void flushUsageTelemetry()
    }
  }, [page])
}

/* ─────────────────── test helper（既存テストに影響しない） ─────────────────── */

/** テスト用: モジュール状態を初期化（実プロダクトでは呼ばない）。 */
export function __resetUsageTelemetryForTest(): void {
  if (flushTimer) {
    clearInterval(flushTimer)
    flushTimer = null
  }
  initialized = false
  flushingPromise = null
  try {
    localStorage.removeItem(LS_KEY)
  } catch {
    /* noop */
  }
}
